export const STAGE = {
  ground: '#14120F',
  groundLift: '#1A1714',
  surface: '#1B1815',
  surface2: '#231F1B',
  line: '#2E2A25',
  lineStrong: '#6E665B',
  ink: '#EFE9DF',
  ink2: '#C6BEB2',
  mute: '#978F84',
  inkRecede: '#5A534A',
  inkFlow: '#8A8277',
  bisque: '#DDD0BB',
  bisqueLight: '#E9DFCF',
  bisqueShade: '#B9A98F',
  focus: '#EFE9DF',
  pillHover: '#E2DBD0',
  frost: 'rgba(20,18,15,.86)',
  frostSolid: 'rgba(20,18,15,.97)',
  selectionBg: '#EFE9DF',
  selectionInk: '#14120F',
} as const;

export const FROST = {
  backgroundColor: STAGE.frost,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
} as const;

export const BISQUE = {
  torus: [
    'radial-gradient(circle closest-side at 50% 47%, rgba(20,18,15,.35) 44%, rgba(20,18,15,0) 58%)',
    'radial-gradient(circle closest-side, rgba(58,46,32,0) 76%, rgba(58,46,32,.16) 100%)',
    `radial-gradient(circle at 30% 26%, ${STAGE.bisqueLight} 0%, ${STAGE.bisque} 46%, ${STAGE.bisqueShade} 100%)`,
  ].join(', '),
  hole: 'radial-gradient(closest-side, transparent 44%, #000 45%)',
  contact: '0 .05em .1em rgba(0,0,0,.45)',
} as const;

const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.3' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 .96 0 0 0 0 .9 0 0 0 .55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

export const ATMOS = {
  grain: GRAIN,
  grainOpacity: 0.04,
} as const;

export const TT = {
  lighter: '#D1E9FC',
  light: '#76B0F1',
  main: '#2065D1',
  field: '#0C1B3D',
  fieldDeep: '#10255A',
} as const;

export const WS = {
  ocean: '#16283F',
  oceanDeep: '#0D1728',
  land: '#EFE1C2',
  landSage: '#DDE6C2',
  landLilac: '#E1D5E7',
  ink: '#241D2E',
  gold: '#F0A73A',
  conflict: '#FF4B4B',
  agreement: '#45A8F0',
} as const;

export const GUTTER = { xs: '16px', sm: '32px', lg: '64px' } as const;

export const CONTAINER = {
  maxWidth: 1440,
  mx: 'auto',
  px: GUTTER,
} as const;

export const FONT = {
  sans: "'Geist', sans-serif",
  mono: "'Geist Mono', monospace",
  serif: "'Fraunces', serif",
} as const;

export const TYPE = {
  wordmarkDisplay: {
    fontFamily: FONT.sans,
    fontWeight: 600,
    fontSize: 'min(calc((100vw - 2 * var(--gutter)) / 5.6), 232px, 27vh)',
    '@supports (width: 1cqw) and (height: 1svh)': {
      fontSize: 'min(calc(100cqw / 5.6), 232px, 27svh)',
    },
    lineHeight: 0.8,
    letterSpacing: '-0.045em',
  },
  heroTitle: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 40, sm: 56, lg: 72 },
    lineHeight: 1.05,
    letterSpacing: { xs: '-0.025em', sm: '-0.03em', lg: '-0.035em' },
  },
  billboard: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 38, sm: 64, lg: 88 },
    lineHeight: { xs: 1.04, sm: 1.02, lg: 1 },
    letterSpacing: { xs: '-0.025em', sm: '-0.03em', lg: '-0.035em' },
  },
  statement: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 28, sm: 40, lg: 56 },
    lineHeight: { xs: 1.2, sm: 1.15, lg: 1.12 },
    letterSpacing: { xs: '-0.025em', sm: '-0.03em', lg: '-0.035em' },
  },
  principleTitle: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 22, sm: 24, lg: 28 },
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
  },
  accent: {
    fontFamily: FONT.serif,
    fontStyle: 'italic',
    fontWeight: 350,
    fontSize: '1.04em',
    lineHeight: 1,
    letterSpacing: '-0.01em',
    fontVariationSettings: "'SOFT' 100, 'opsz' 144",
  },
  lead: {
    fontFamily: FONT.sans,
    fontWeight: 400,
    fontSize: { xs: 17, sm: 19, lg: 21 },
    lineHeight: { xs: '26px', sm: '28px', lg: '31px' },
    letterSpacing: { xs: 0, sm: '-0.003em', lg: '-0.005em' },
  },
  body: {
    fontFamily: FONT.sans,
    fontWeight: 400,
    fontSize: { xs: 15, sm: 17 },
    lineHeight: { xs: '23px', sm: '26px' },
    letterSpacing: 0,
  },
  label: {
    fontFamily: FONT.mono,
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '16px',
    letterSpacing: '.08em',
    textTransform: 'uppercase',
  },
  fine: {
    fontFamily: FONT.mono,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '18px',
    letterSpacing: '.02em',
  },
  navWordmark: {
    fontFamily: FONT.sans,
    fontWeight: 600,
    fontSize: { xs: 17, sm: 18 },
    lineHeight: 1,
    letterSpacing: '-0.02em',
  },
  button: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 16, sm: 17 },
    lineHeight: 1,
    letterSpacing: 0,
  },
} as const;
